import { Injectable, ErrorHandler, inject, isDevMode } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from './notification.service';

export interface ErrorInfo {
  message: string;
  stack?: string;
  url?: string;
  timestamp: Date;
  userAgent: string;
  userId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandler implements ErrorHandler {
  private notificationService = inject(NotificationService);

  handleError(error: unknown): void {
    console.error('Global error caught:', error);
    const errorRecord = this.toRecord(error);

    // Create error info object
    const errorInfo: ErrorInfo = {
      message: this.getErrorMessage(error),
      stack: this.readString(errorRecord?.['stack']) ?? undefined,
      url: window.location.href,
      timestamp: new Date(),
      userAgent: navigator.userAgent
    };

    // Log error for debugging
    this.logError(errorInfo, error);

    // Handle different error types
    if (error instanceof HttpErrorResponse) {
      this.handleHttpError(error);
    } else if (error instanceof TypeError) {
      this.handleTypeError(error);
    } else if (error instanceof ReferenceError) {
      this.handleReferenceError(error);
    } else if (errorRecord?.['rejection'] !== undefined || errorRecord?.['reason'] !== undefined) {
      // Unhandled promise rejection
      this.handlePromiseRejection(error);
    } else {
      this.handleGenericError(error);
    }

    // Send error to monitoring service (if implemented)
    this.sendErrorToMonitoring(errorInfo);
  }

  private getErrorMessage(error: unknown): string {
    const errorRecord = this.toRecord(error);
    const message = this.readString(errorRecord?.['message']);
    if (message) {
      return message;
    }
    
    const nestedError = this.toRecord(errorRecord?.['error']);
    const nestedMessage = this.readString(nestedError?.['message']);
    if (nestedMessage) {
      return nestedMessage;
    }
    
    if (typeof error === 'string') {
      return error;
    }
    
    return 'An unexpected error occurred';
  }

  private handleHttpError(error: HttpErrorResponse): void {
    // HTTP errors are already handled by the ErrorInterceptor
    // This is a fallback for any HTTP errors that slip through
    console.error('HTTP Error in Global Handler:', {
      status: error.status,
      message: error.message,
      url: error.url
    });
  }

  private handleTypeError(error: TypeError): void {
    console.error('Type Error:', error);
    
    // Check for common type errors
    if (error.message.includes('Cannot read property') || 
        error.message.includes('Cannot read properties')) {
      this.notificationService.showError('A data loading error occurred. Please refresh the page.');
    } else if (error.message.includes('is not a function')) {
      this.notificationService.showError('A functionality error occurred. Please try again.');
    } else {
      this.notificationService.showError('An unexpected error occurred. Please refresh the page.');
    }
  }

  private handleReferenceError(error: ReferenceError): void {
    console.error('Reference Error:', error);
    this.notificationService.showError('A system error occurred. Please refresh the page.');
  }

  private handlePromiseRejection(error: unknown): void {
    console.error('Unhandled Promise Rejection:', error);
    
    const errorRecord = this.toRecord(error);
    const rejectionReason = errorRecord?.['rejection'] ?? errorRecord?.['reason'];
    
    if (rejectionReason instanceof HttpErrorResponse) {
      // HTTP promise rejection - already handled by interceptor
      return;
    }
    
    this.notificationService.showError('An operation failed. Please try again.');
  }

  private handleGenericError(error: unknown): void {
    console.error('Generic Error:', error);
    const errorRecord = this.toRecord(error);
    const errorName = this.readString(errorRecord?.['name']);
    const errorMessage = this.readString(errorRecord?.['message']) ?? '';
    
    // Check for specific error patterns
    if (errorName === 'ChunkLoadError') {
      this.notificationService.showError('Failed to load application resources. Please refresh the page.');
      return;
    }
    
    if (errorMessage.includes('Loading chunk')) {
      this.notificationService.showError('Failed to load page resources. Please refresh the page.');
      return;
    }
    
    if (errorMessage.includes('Script error')) {
      // Cross-origin script errors - usually not actionable for users
      console.warn('Cross-origin script error detected');
      return;
    }
    
    // Generic fallback
    this.notificationService.showError('An unexpected error occurred. Please try again.');
  }

  private logError(errorInfo: ErrorInfo, originalError: unknown): void {
    // Enhanced logging for development
    if (this.isDevelopment()) {
      console.group('🚨 Global Error Handler');
      console.error('Error Info:', errorInfo);
      console.error('Original Error:', originalError);
      console.trace('Stack Trace');
      console.groupEnd();
    }
    
    // In production, you might want to send this to a logging service
    // Example: this.loggingService.logError(errorInfo);
  }

  private sendErrorToMonitoring(errorInfo: ErrorInfo): void {
    void errorInfo;
    // In a real application, you would send this to a monitoring service
    // like Sentry, LogRocket, or a custom error tracking service
    
    // Example implementation:
    // this.monitoringService.captureError(errorInfo);
    
    // For now, we'll just log it
    if (!this.isDevelopment()) {
      // Only send to monitoring in production
    }
  }

  private isDevelopment(): boolean {
    // Hostname checks break on whitelabel domains; use the build flag.
    return isDevMode();
  }

  /**
   * Manually report an error
   */
  reportError(error: Error, context?: string): void {
    const enhancedError = new Error(`${context ? `[${context}] ` : ''}${error.message}`);
    enhancedError.stack = error.stack;
    this.handleError(enhancedError);
  }

  /**
   * Report a custom error with additional context
   */
  reportCustomError(message: string, context?: unknown): void {
    const error = new Error(message);
    if (context) {
      (error as Error & { context?: unknown }).context = context;
    }
    this.handleError(error);
  }

  private toRecord(value: unknown): Record<string, unknown> | null {
    if (typeof value === 'object' && value !== null) {
      return value as Record<string, unknown>;
    }
    return null;
  }

  private readString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
}
